import { useRef } from "react";
import {
  motion,
  useMotionValue,
  useSpring,
  useTransform,
  type MotionValue,
} from "framer-motion";
import { Lock } from "lucide-react";
import { DOCK_ORDER, APPS } from "@/data/apps";
import { useHive } from "@/store/useHive";
import { appState, isVisible } from "@/lib/entitlements";
import type { AppId } from "@/types";

const BASE = 46;
const MAX = 72;
const DISTANCE = 130;

const BADGE_TONE: Partial<Record<AppId, string>> = {
  tasks: "#f59e0b",
  integrations: "#f43f5e",
  workspaces: "#f97316",
};

// Select a primitive count (stable under Object.is) and build the badge object
// outside the selector — returning a fresh object from the selector would break
// useSyncExternalStore's snapshot caching.
function useDockBadge(appId: AppId): { count: number; tone: string } | null {
  const count = useHive((s) => {
    if (appId === "tasks")
      return s.tasks.filter((t) => t.status === "needs-clarification" || t.status === "blocked").length;
    if (appId === "integrations")
      return s.integrations.filter((i) => i.status === "degraded" || i.status === "needs-reauth" || i.status === "pending").length;
    if (appId === "workspaces")
      return s.codeWorkspaces.filter((w) => w.status === "error" || w.status === "conflict" || w.status === "needs-sync").length;
    return 0;
  });
  return count ? { count, tone: BADGE_TONE[appId] ?? "#f59e0b" } : null;
}

function DockIcon({
  appId,
  mouseX,
}: {
  appId: AppId;
  mouseX: MotionValue<number>;
}) {
  const ref = useRef<HTMLButtonElement>(null);
  const def = APPS[appId];
  const openApp = useHive((s) => s.openApp);
  const isOpen = useHive((s) => s.windows.some((w) => w.appId === appId));
  const badge = useDockBadge(appId);
  const gated = useHive((s) => {
    const st = appState(s.entitlements, appId);
    return st === "preview" || st === "locked";
  });

  const distance = useTransform(mouseX, (x) => {
    const bounds = ref.current?.getBoundingClientRect() ?? { x: 0, width: BASE };
    return x - bounds.x - bounds.width / 2;
  });

  const sizeRaw = useTransform(distance, [-DISTANCE, 0, DISTANCE], [BASE, MAX, BASE]);
  const size = useSpring(sizeRaw, { stiffness: 320, damping: 22, mass: 0.5 });
  const liftRaw = useTransform(distance, [-DISTANCE, 0, DISTANCE], [0, -12, 0]);
  const lift = useSpring(liftRaw, { stiffness: 320, damping: 22, mass: 0.5 });

  return (
    <div className="group relative flex flex-col items-center justify-end">
      {/* tooltip */}
      <motion.div
        className="pointer-events-none absolute -top-9 whitespace-nowrap rounded-lg border border-app bg-surface px-2.5 py-1 text-xs font-medium text-app opacity-0 shadow-lg transition group-hover:opacity-100"
        style={{ y: lift }}
      >
        {def.name}
        {gated && <span className="ml-1.5 text-[10px] font-semibold text-amber-500">Early Access</span>}
      </motion.div>

      <motion.button
        ref={ref}
        style={{ width: size, height: size, y: lift }}
        onClick={() => openApp(appId)}
        whileTap={{ scale: 0.86 }}
        className="relative grid place-items-center rounded-2xl text-white shadow-lg"
      >
        <div
          className="absolute inset-0 rounded-2xl"
          style={{
            background: `linear-gradient(135deg, ${def.accent}, ${def.accent}99)`,
            boxShadow: `inset 0 1px 0 rgba(255,255,255,0.25), 0 8px 20px -6px ${def.accent}aa`,
          }}
        />
        <motion.div style={{ width: useTransform(size, (s) => s * 0.46) }} className="relative">
          <def.icon className="h-full w-full" style={{ width: "100%", height: "auto" }} />
        </motion.div>

        {/* badge — count of items needing attention */}
        {badge && (
          <motion.span
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 400, damping: 18 }}
            className="absolute -right-1 -top-1 grid h-4 min-w-4 place-items-center rounded-full px-1 text-[9px] font-bold text-white shadow-md ring-2 ring-[hsl(var(--bg-0))]"
            style={{ background: badge.tone }}
          >
            {badge.count}
          </motion.span>
        )}

        {/* lock glyph — early-access products keep branding but show the gate */}
        {gated && (
          <span className="absolute -bottom-1 -right-1 grid h-4 w-4 place-items-center rounded-full bg-[hsl(var(--bg-0))] shadow-md ring-1 ring-[hsl(var(--border))]">
            <Lock size={9} className="text-amber-500" />
          </span>
        )}
      </motion.button>

      {/* active indicator */}
      <div className="mt-1 h-1 w-1">
        {isOpen && (
          <motion.div
            layoutId={`dot-${appId}`}
            className="h-1 w-1 rounded-full"
            style={{ background: def.accent }}
          />
        )}
      </div>
    </div>
  );
}

export function Dock() {
  const mouseX = useMotionValue(Infinity);
  // Hidden products are not rendered anywhere — including the dock.
  const entitlements = useHive((s) => s.entitlements);
  const visibleApps = DOCK_ORDER.filter((id) => isVisible(entitlements, id));
  return (
    <div className="pointer-events-none absolute inset-x-0 bottom-0 z-40 flex justify-center pb-3">
      <motion.div
        onMouseMove={(e) => mouseX.set(e.clientX)}
        onMouseLeave={() => mouseX.set(Infinity)}
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: "spring", stiffness: 260, damping: 26, delay: 0.3 }}
        className="glass-strong pointer-events-auto flex items-end gap-2 rounded-[26px] border border-app px-3 pt-2.5 shadow-dock"
      >
        {visibleApps.map((id) => (
          <DockIcon key={id} appId={id} mouseX={mouseX} />
        ))}
      </motion.div>
    </div>
  );
}
